import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import './Hero.css';

/*
 * Terminal script — the signature element of the page.
 * type: 'cmd'  → typed character by character after the prompt
 * type: 'out'  → printed as output
 * type: 'ok'   → success line
 */
const SCRIPT = [
  { type: 'cmd', text: 'ssh deploy@loga.hr' },
  { type: 'cmd', text: 'cd /var/www/client-app && php artisan deploy' },
  { type: 'out', text: '→ building assets .......... done (3.2s)' },
  { type: 'out', text: '→ running migrations ........ done' },
  { type: 'out', text: '→ cache warmed · queue restarted' },
  { type: 'ok', text: '✓ deployed — uptime 99.98%' },
];

const TYPE_SPEED = 34; // ms per character
const LINE_PAUSE = 420; // ms between lines

const Terminal = () => {
  const [lines, setLines] = useState([]);
  const [typing, setTyping] = useState('');
  const [done, setDone] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setLines(SCRIPT);
      setDone(true);
      return;
    }

    let cancelled = false;
    const timeouts = [];
    const wait = (ms) =>
      new Promise((resolve) => {
        timeouts.push(setTimeout(resolve, ms));
      });

    const run = async () => {
      await wait(700);
      for (const line of SCRIPT) {
        if (cancelled) return;

        if (line.type === 'cmd') {
          for (let i = 1; i <= line.text.length; i++) {
            if (cancelled) return;
            setTyping(line.text.slice(0, i));
            await wait(TYPE_SPEED);
          }
          await wait(180);
          setTyping('');
        }

        setLines((prev) => [...prev, line]);
        await wait(line.type === 'cmd' ? 260 : LINE_PAUSE);
      }
      if (!cancelled) setDone(true);
    };

    run();
    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [lines, typing]);

  return (
    <div className="terminal" aria-hidden="true">
      <div className="terminal-bar">
        <span className="terminal-dot dot-red"></span>
        <span className="terminal-dot dot-amber"></span>
        <span className="terminal-dot dot-green"></span>
        <span className="terminal-title">deploy — loga.hr</span>
      </div>
      <div className="terminal-body" ref={bodyRef}>
        {lines.map((line, i) => (
          <div key={i} className={`terminal-line line-${line.type}`}>
            {line.type === 'cmd' && <span className="prompt">$</span>}
            {line.text}
          </div>
        ))}
        {typing && (
          <div className="terminal-line line-cmd">
            <span className="prompt">$</span>
            {typing}
            <span className="cursor"></span>
          </div>
        )}
        {done && (
          <div className="terminal-line line-cmd">
            <span className="prompt">$</span>
            <span className="cursor"></span>
          </div>
        )}
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="hero" id="hero">
      <div className="hero-glow" aria-hidden="true"></div>
      <div className="hero-grid-pattern" aria-hidden="true"></div>

      <div className="container">
        <div className="hero-layout">
          <div className="hero-content">
            <div className="hero-eyebrow fade-in delay-100">
              <span className="eyebrow-prompt">~/loga</span>
              <span className="eyebrow-sep">·</span>
              Software &amp; Infrastructure
            </div>

            <h1 className="fade-in-up delay-200">
              {t('hero.title')}
              <br />
              <span className="hero-highlight">{t('hero.subtitle')}</span>
            </h1>

            <p className="hero-description fade-in-up delay-300">
              {t('hero.description')}
            </p>

            <div className="hero-buttons fade-in-up delay-400">
              <Link
                to="contact"
                smooth={true}
                duration={600}
                offset={-80}
                className="btn btn-primary btn-large"
              >
                <span className="btn-text">{t('hero.cta')}</span>
                <span className="btn-icon">→</span>
              </Link>
              <Link
                to="services"
                smooth={true}
                duration={600}
                offset={-80}
                className="btn btn-secondary btn-large"
              >
                <span className="btn-text">{t('hero.learnMore')}</span>
              </Link>
            </div>

            <div className="hero-stats fade-in delay-500">
              <div className="stat-item">
                <span className="stat-value">10+</span>
                <span className="stat-label">{t('hero.stats.years')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">50+</span>
                <span className="stat-label">{t('hero.stats.projects')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">24/7</span>
                <span className="stat-label">{t('hero.stats.support')}</span>
              </div>
            </div>
          </div>

          <div className="hero-visual fade-in-up delay-300">
            <Terminal />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
